import React, { useEffect, useRef, useState } from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import { Link } from "react-router-dom";

import {
  getPhoneFinderOptions,
  getPhoneFinderRecommendations,
} from "../api/apiCalls";
import { getPhoneUrl } from "../utils/productUrl";

function parsePrice(product) {
  if (typeof product?.finderPrice === "number") {
    return product.finderPrice;
  }

  if (product?.priceStorage?.length) {
    return Math.min(
      ...product.priceStorage.map((item) => Number(item.price) || 0)
    );
  }

  return Number(product?.price) || 0;
}

function getProductUrl(product) {
  return getPhoneUrl(product);
}

function buildQuestionFlowFromOptions(response) {
  const priceRanges = Array.isArray(response?.priceRanges)
    ? response.priceRanges
    : [];
  const brands = Array.isArray(response?.brands) ? response.brands : [];

  if (!priceRanges.length || !brands.length) {
    return [];
  }

  return [
    {
      id: "budget",
      question: "Колкав буџет имаш за нов телефон?",
      options: priceRanges,
    },
    {
      id: "brand",
      question: "Дали преферираш одреден бренд?",
      options: brands,
    },
    {
      id: "usage",
      question: "За што најмногу ќе го користиш телефонот?",
      options: [
        { value: "camera", label: "Добра камера (фотографии/видео)" },
        { value: "battery", label: "Батерија што трае долго" },
        { value: "gaming", label: "Гејминг и брзи перформанси" },
        { value: "everyday", label: "Секојдневна употреба" },
      ],
    },
    {
      id: "size",
      question: "Каква големина на екран бараш?",
      options: [
        { value: "compact", label: "Компактен" },
        { value: "balanced", label: "Средна големина" },
        { value: "large", label: "Голем екран" },
        { value: "any", label: "Не ми е важно" },
      ],
    },
  ];
}

function PhoneFinderAssistant() {
  const QUESTION_TYPING_DELAY = 900;
  const [isOpen, setIsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [questionFlow, setQuestionFlow] = useState([]);
  const [isAvailable, setIsAvailable] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [answers, setAnswers] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingResults, setLoadingResults] = useState(false);
  const [isQuestionTyping, setIsQuestionTyping] = useState(false);
  const [showRecommendationSlider, setShowRecommendationSlider] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);
  const cardsRef = useRef(null);
  const previousStepIndexRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    setLoadingOptions(true);

    getPhoneFinderOptions().then((response) => {
      if (!isMounted) {
        return;
      }

      const remoteQuestionFlow = Array.isArray(response?.questionFlow)
        ? response.questionFlow
        : buildQuestionFlowFromOptions(response);

      if (!remoteQuestionFlow.length) {
        setIsAvailable(false);
        setLoadingOptions(false);
        return;
      }

      setQuestionFlow(remoteQuestionFlow);
      setIsAvailable(true);
      setLoadingOptions(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const currentStepIndex = Object.keys(answers).length;
  const currentQuestion = questionFlow[currentStepIndex];
  const isComplete = currentStepIndex >= questionFlow.length;

  useEffect(() => {
    if (!questionFlow.length || !isComplete) {
      return;
    }

    let isMounted = true;
    setLoadingResults(true);

    getPhoneFinderRecommendations(answers).then((response) => {
      if (!isMounted) {
        return;
      }

      setRecommendations(
        Array.isArray(response?.recommendations) ? response.recommendations : []
      );
      setAiSummary(response?.summary || "");
      setLoadingResults(false);
    });

    return () => {
      isMounted = false;
    };
  }, [answers, isComplete, questionFlow.length]);

  useEffect(() => {
    if (!isComplete || loadingResults || !recommendations.length) {
      setShowRecommendationSlider(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowRecommendationSlider(true);
    }, 700);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isComplete, loadingResults, recommendations]);

  useEffect(() => {
    if (!isOpen || loadingOptions || !isAvailable || isComplete) {
      setIsQuestionTyping(false);
      previousStepIndexRef.current = currentStepIndex;
      return undefined;
    }

    const shouldAnimateNextQuestion =
      currentStepIndex === 0 || currentStepIndex !== previousStepIndexRef.current;

    previousStepIndexRef.current = currentStepIndex;

    if (!shouldAnimateNextQuestion) {
      setIsQuestionTyping(false);
      return undefined;
    }

    setIsQuestionTyping(true);

    const timeoutId = window.setTimeout(() => {
      setIsQuestionTyping(false);
    }, QUESTION_TYPING_DELAY);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    QUESTION_TYPING_DELAY,
    currentStepIndex,
    isAvailable,
    isComplete,
    isOpen,
    loadingOptions,
  ]);

  useEffect(() => {
    if (!isOpen || !messagesEndRef.current) {
      return;
    }

    messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [
    answers,
    aiSummary,
    isOpen,
    isQuestionTyping,
    loadingOptions,
    loadingResults,
    questionFlow,
    recommendations,
  ]);

  const selectedAnswerMap = questionFlow.reduce((accumulator, item) => {
    const selectedValue = answers[item.id];
    accumulator[item.id] =
      item.options.find((option) => option.value === selectedValue) || null;
    return accumulator;
  }, {});

  const handleOptionSelect = (questionId, optionValue) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: optionValue,
    }));
    setShowWelcome(false);
  };

  const handleReset = () => {
    setAnswers({});
    setRecommendations([]);
    setAiSummary("");
    setLoadingResults(false);
    setIsQuestionTyping(false);
    setShowRecommendationSlider(false);
    setShowWelcome(true);
    previousStepIndexRef.current = 0;
  };

  const scrollRecommendations = (direction) => {
    if (!cardsRef.current) {
      return;
    }

    const scrollAmount = Math.max(cardsRef.current.clientWidth * 0.82, 220);
    cardsRef.current.scrollBy({
      left: direction * scrollAmount,
      behavior: "smooth",
    });
  };

  const openAssistant = () => {
    setShowPreview(false);
    setIsOpen(true);
  };

  if (loadingOptions || !isAvailable) {
    return null;
  }

  return (
    <div className="phone-finder">
      {!isOpen && showPreview ? (
        <div
          className="phone-finder__preview"
          onClick={openAssistant}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openAssistant();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Open phone finder assistant"
        >
          <span className="phone-finder__preview-dismiss-wrap">
            <button
              type="button"
              className="phone-finder__preview-dismiss"
              onClick={(event) => {
                event.stopPropagation();
                setShowPreview(false);
              }}
              aria-label="Close preview"
            >
              <CloseIcon fontSize="inherit" />
            </button>
          </span>
          <span className="phone-finder__preview-title">
            Здраво, јас сум Buzz 👋
          </span>
          <span className="phone-finder__preview-text">
            Можам да ти помогнам да го пронајдеш идеалниот телефон за тебе.
          </span>
        </div>
      ) : null}

      {isOpen ? (
        <div
          className="phone-finder__panel"
          aria-label="Phone finder assistant"
        >
          <div className="phone-finder__header">
            <span className="phone-finder__eyebrow">
              Buzz Mobile - Пронајди телефон
            </span>

            <div className="phone-finder__header-actions">
              <button
                type="button"
                className="phone-finder__icon-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close phone finder"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          <div className="phone-finder__messages">
            {showWelcome && !Object.keys(answers).length ? (
              <div className="phone-finder__bubble phone-finder__bubble--assistant">
                Здраво, јас сум Buzz. 👋 <br /> Можам да ти помогнам да го
                пронајдеш идеалниот телефон за тебе.
              </div>
            ) : null}

            {loadingOptions ? (
              <div className="phone-finder__bubble phone-finder__bubble--assistant phone-finder__bubble--loading">
                <CircularProgress size={18} />
                <span>Ги подготвувам изборите...</span>
              </div>
            ) : null}

            {questionFlow.map((item, index) => {
              const selectedAnswer = selectedAnswerMap[item.id];
              const isTypingStep =
                currentQuestion?.id === item.id && isQuestionTyping;
              const isActiveStep =
                currentQuestion?.id === item.id && !isQuestionTyping;

              if (!selectedAnswer && !isActiveStep && !isTypingStep) {
                return null;
              }

              return (
                <div key={item.id} className="phone-finder__step">
                  {isTypingStep ? (
                    <div className="phone-finder__bubble phone-finder__bubble--assistant phone-finder__bubble--loading">
                      <span className="phone-finder__typing">
                        <span className="phone-finder__typing-dot" />
                        <span className="phone-finder__typing-dot" />
                        <span className="phone-finder__typing-dot" />
                      </span>
                    </div>
                  ) : null}

                  {!isTypingStep ? (
                    <div className="phone-finder__bubble phone-finder__bubble--assistant">
                      <span className="phone-finder__step-no">0{index + 1}</span>
                      <p>{item.question}</p>
                    </div>
                  ) : null}

                  {selectedAnswer ? (
                    <div className="phone-finder__bubble phone-finder__bubble--user">
                      {selectedAnswer.label}
                    </div>
                  ) : null}

                  {isActiveStep ? (
                    <div className="phone-finder__options">
                      {item.options.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="phone-finder__option"
                          onClick={() =>
                            handleOptionSelect(item.id, option.value)
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {loadingResults ? (
              <div className="phone-finder__bubble phone-finder__bubble--assistant phone-finder__bubble--loading">
                <span className="phone-finder__typing">
                  <span className="phone-finder__typing-dot" />
                  <span className="phone-finder__typing-dot" />
                  <span className="phone-finder__typing-dot" />
                </span>
              </div>
            ) : null}

            {isComplete && !loadingResults ? (
              <div className="phone-finder__results">
                <div className="phone-finder__bubble phone-finder__bubble--assistant">
                  Еве ги моделите што најдобро одговараат на твоите избори.
                </div>

                {!showRecommendationSlider ? (
                  <div className="phone-finder__bubble phone-finder__bubble--assistant phone-finder__bubble--loading">
                    <span className="phone-finder__typing">
                      <span className="phone-finder__typing-dot" />
                      <span className="phone-finder__typing-dot" />
                      <span className="phone-finder__typing-dot" />
                    </span>
                  </div>
                ) : null}

                {showRecommendationSlider ? (
                  <div className="phone-finder__slider-controls">
                    <button
                      type="button"
                      className="phone-finder__slider-btn"
                      onClick={() => scrollRecommendations(-1)}
                      aria-label="Previous phone recommendations"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      className="phone-finder__slider-btn"
                      onClick={() => scrollRecommendations(1)}
                      aria-label="Next phone recommendations"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </button>
                  </div>
                ) : null}

                <div
                  ref={cardsRef}
                  className={`phone-finder__cards${
                    showRecommendationSlider
                      ? " phone-finder__cards--visible"
                      : ""
                  }`}
                >
                  {recommendations.map((product) => (
                    <article key={product._id} className="phone-finder__card">
                      <div className="phone-finder__card-top">
                        <span>{product.brand}</span>
                        <strong>
                          {parseFloat(parsePrice(product)).toLocaleString("en")}{" "}
                          ден.
                        </strong>
                      </div>

                      <img src={product.img?.[0]} alt={product.title} />
                      <h4>{product.title}</h4>
                      <p>{product.display}</p>
                      <p>{product.camera}</p>

                      <Link
                        to={getProductUrl(product)}
                        className="phone-finder__card-link"
                        onClick={() => setIsOpen(false)}
                      >
                        Види модел
                      </Link>
                    </article>
                  ))}
                </div>

                <div className="phone-finder__ai-box">
                  <div className="phone-finder__ai-title">
                    <AutoAwesomeIcon fontSize="small" />
                    <span>BUZZ - препорака</span>
                  </div>

                  <p>{aiSummary}</p>
                </div>

                <button
                  type="button"
                  className="phone-finder__retry-btn"
                  onClick={handleReset}
                >
                  Пронајди друг телефон
                </button>
              </div>
            ) : null}

            <div ref={messagesEndRef} />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={`phone-finder__trigger${
          isOpen ? " phone-finder__trigger--open" : ""
        }`}
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            return;
          }

          openAssistant();
        }}
        aria-label={isOpen ? "Close phone finder" : "Open phone finder"}
      >
        <ChatBubbleOutlineIcon />
      </button>
    </div>
  );
}

export default PhoneFinderAssistant;
